import React, { useEffect, useRef, useState } from 'react';

const ProcessSection = () => {
  const flowRef = useRef(null);
  const [fillHeight, setFillHeight] = useState('0px');

  useEffect(() => {
    const handleScroll = () => {
      const flowEl = flowRef.current;
      if (!flowEl) return;

      const rect = flowEl.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // Start progress when top of timeline hits 62% of viewport height
      // End progress when bottom of timeline hits 78% of viewport height
      const startTrigger = viewportHeight * 0.62;
      const endTrigger = viewportHeight * 0.78;

      const elementHeight = rect.height;
      const scrollRange = elementHeight - (endTrigger - startTrigger);
      const scrolled = startTrigger - rect.top;

      let progress = scrolled / scrollRange;
      if (progress < 0) progress = 0;
      if (progress > 1) progress = 1;

      // Calculate fill height: max is offsetHeight - 28
      const maxFillHeight = elementHeight - 28;
      const currentFillHeight = progress * maxFillHeight;
      setFillHeight(`${currentFillHeight}px`);

      // Determine 'is-live' state for each step circle:
      // Active when step top crosses 68% of screen and stays active until bottom crosses 42% of screen
      const stepElements = flowEl.querySelectorAll('.step');
      stepElements.forEach((stepEl) => {
        const stepRect = stepEl.getBoundingClientRect();
        const isLive = stepRect.top < viewportHeight * 0.68 && stepRect.bottom > viewportHeight * 0.42;
        if (isLive) {
          stepEl.classList.add('is-live');
        } else {
          stepEl.classList.remove('is-live');
        }
      });
    };

    let ticking = false;
    const onScrollOrResize = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });

    // Trigger on mount
    handleScroll();

    return () => {
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, []);

  return (
    <section className="sec" id="process">
      <div className="shell">
        <div className="sec-head rv">
          <h2 className="h2">How does the Corporate Tax filing process work?</h2>
          <p className="answer">
            Four steps, usually inside two weeks. A free 20 minute call establishes your position, we confirm it in
            writing with a fixed fee before any work begins, you send one set of documents, and we file on EmaraTax and
            send you the confirmation and working papers.
          </p>
        </div>

        <div className="flow-wrap">
          <div className="flow" id="flow" ref={flowRef}>
            <span
              className="flow-fill"
              id="flowFill"
              aria-hidden="true"
              style={{
                height: fillHeight,
                transition: 'height 0.2s ease-out'
              }}
            ></span>

            <div className="step">
              <div className="step-num">1</div>
              <div className="step-body">
                <h3>A free 20 minute call</h3>
                <p>
                  We ask for your trade licence, your financial year end, your last set of accounts and whether you
                  already hold a Corporate Tax Registration Number. Nothing else.
                </p>
                <span className="when">Within one business day</span>
              </div>
            </div>

            <div className="step">
              <div className="step-num">2</div>
              <div className="step-body">
                <h3>Your position, confirmed in writing</h3>
                <p>
                  We tell you whether you must register, whether you can elect Small Business Relief, your exact
                  deadline and the fixed fee. You decide from there.
                </p>
                <span className="when">Same week</span>
              </div>
            </div>

            <div className="step">
              <div className="step-num">3</div>
              <div className="step-body">
                <h3>You upload documents once</h3>
                <p>
                  One request list, not five. We prepare the computation and the return, and come back to you only
                  where something genuinely needs your decision.
                </p>
                <span className="when">You spend about an hour</span>
              </div>
            </div>

            <div className="step">
              <div className="step-num">4</div>
              <div className="step-body">
                <h3>Filed on EmaraTax, with proof in your inbox</h3>
                <p>
                  We submit the return and send you the submission confirmation, the filed return and the working
                  papers to keep for your records.
                </p>
                <span className="when">Before your deadline</span>
              </div>
            </div>
          </div>

          <figure className="flow-media rv" style={{ margin: 0 }}>
            <img
              src="/process.webp"
              alt="An accountant signing a document at a desk"
              width="1000"
              height="1250"
              loading="lazy"
              decoding="async"
            />
            <figcaption>
              <b>You send documents once</b>
              The request list is fixed at step two, so there is no drip-feed of follow-up emails in the last week
              before the deadline.
            </figcaption>
          </figure>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
